'use client';

import { FC } from 'react';
import type { ImageField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';
import {
  CalloutBody,
  CalloutCardShell,
} from '@/slices/SplitParagraphCallout/components';

type CaseQuoteBlockPrimary = {
  description?: import('@prismicio/client').RichTextField;
  title?: string;
  remove_bottom_padding?: boolean;
};

type CaseQuoteBlockSlice = {
  slice_type: string;
  variation: string;
  primary: CaseQuoteBlockPrimary;
};

// Empty image field: `CalloutCardShell` falls back to the green gradient when
// no background image is set, which is exactly the visual we want here.
const EMPTY_BACKGROUND_IMAGE: ImageField = {
  url: null,
} as unknown as ImageField;

const CaseQuoteBlock: FC<{ slice: CaseQuoteBlockSlice }> = ({ slice }) => {
  const { description, title, remove_bottom_padding } = slice.primary;
  // Same fallback pattern as the other case-study slices: defaults to true
  // so the quote sits flush against the next slice, and legacy documents
  // where the field isn't saved get the same behavior.
  const removeBottomPadding = remove_bottom_padding ?? true;

  if (!isFilled.richText(description) && !title) {
    return null;
  }

  return (
    <CalloutCardShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      removeUpperMargin={false}
      removeLowerMargin={removeBottomPadding}
      backgroundImage={EMPTY_BACKGROUND_IMAGE}
    >
      <div className="flex w-full flex-col items-start gap-8 p-[15px] md:flex-row md:items-center md:gap-12">
        <RevealOnScroll className="w-full md:w-auto md:shrink-0 md:px-[60px]">
          {/* Double-quote glyph, white on the green gradient. Sized to match
              the Figma reference (55×38). */}
          <svg
            width="55"
            height="38"
            viewBox="0 0 55 38"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M0 28.9098C0 19.2732 1.79974 12.1699 5.39921 7.6C9.43063 2.53333 16.0057 0 25.1243 0V11.9216C19.0772 11.9216 15.5257 15.1503 14.4699 21.6078C14.086 23.6941 13.894 26.1281 13.894 28.9098V38H0V28.9098ZM29.8757 28.9098C29.8757 19.2732 31.6754 12.1699 35.2749 7.6C39.3063 2.53333 45.8813 0 55 0V11.9216C48.9529 11.9216 45.4014 15.1503 44.3455 21.6078C43.9616 23.6941 43.7696 26.1281 43.7696 28.9098V38H29.8757V28.9098Z"
              fill="white"
            />
          </svg>
        </RevealOnScroll>

        <div className="flex w-full flex-col gap-3 md:min-w-0 md:flex-1">
          {isFilled.richText(description) && (
            <CalloutBody body={description} delay={200} />
          )}

          {title && (
            <RevealOnScroll
              delay={300}
              className="w-full text-[14px] font-normal uppercase leading-[1.2] text-white whitespace-pre-line"
            >
              {title}
            </RevealOnScroll>
          )}
        </div>
      </div>
    </CalloutCardShell>
  );
};

export default CaseQuoteBlock;
